import { useState, useRef, useCallback, useEffect } from "react";

interface UseMediaRecorderProps {
  stream: MediaStream | null;
  onSave?: (blob: Blob) => void;
}

interface RollingBufferConfig {
  enabled: boolean;
  durationMs: number; // Duration to keep in buffer (e.g., 2 mins = 120000)
}

export function useMediaRecorder({ stream }: UseMediaRecorderProps) {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [rollingBufferConfig, setRollingBufferConfig] =
    useState<RollingBufferConfig>({
      enabled: false,
      durationMs: 120000, // Default 2 mins
    });

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const timerIntervalRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  // Rolling Buffer Refs
  const rollingRecorderRef = useRef<MediaRecorder | null>(null);
  const rollingChunksRef = useRef<
    { data: Blob; timestamp: number; duration: number }[]
  >([]);
  const headerBlobRef = useRef<Blob | null>(null);

  // Determine supported mime type
  const getSupportedMimeType = useCallback(() => {
    const types = [
      "video/webm;codecs=vp9,opus",
      "video/webm;codecs=vp8,opus",
      "video/webm",
      "video/mp4",
    ];
    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }
    return "";
  }, []);

  // --- Manual Recording ---

  const startRecording = useCallback(() => {
    if (!stream) return;

    const mimeType = getSupportedMimeType();
    if (!mimeType) {
      console.error("No supported mime type found");
      return;
    }

    try {
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks: Blob[] = [];

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `recording-${new Date().toISOString()}.webm`; // Extension might need logic if mp4
        if (mimeType.includes("mp4")) {
          a.download = `recording-${new Date().toISOString()}.mp4`;
        }
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      startTimeRef.current = Date.now();

      timerIntervalRef.current = window.setInterval(() => {
        setRecordingTime(
          Math.floor((Date.now() - startTimeRef.current) / 1000)
        );
      }, 1000);
    } catch (err) {
      console.error("Failed to start recording:", err);
    }
  }, [stream, getSupportedMimeType]);

  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== "inactive"
    ) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setRecordingTime(0);
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  // --- Rolling Buffer Logic ---

  // Start/Stop rolling recorder based on config and stream availability
  useEffect(() => {
    if (!rollingBufferConfig.enabled || !stream) {
      // Cleanup if disabled or stream lost
      if (
        rollingRecorderRef.current &&
        rollingRecorderRef.current.state !== "inactive"
      ) {
        rollingRecorderRef.current.stop();
      }
      rollingRecorderRef.current = null;
      rollingChunksRef.current = [];
      headerBlobRef.current = null;
      return;
    }

    // Already running with same stream? (Simplification: restart if stream changes)
    if (
      rollingRecorderRef.current &&
      rollingRecorderRef.current.state === "recording"
    ) {
      return;
    }

    const mimeType = getSupportedMimeType();
    if (!mimeType) return;

    try {
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunkDurationMs = 2000; // 2 second chunks

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          // Attempt to capture header if missing (first chunk usually contains header)
          if (!headerBlobRef.current) {
            // For some codecs, the first chunk is the header.
            // We'll store it separately.
            headerBlobRef.current = e.data;
          }

          rollingChunksRef.current.push({
            data: e.data,
            timestamp: Date.now(),
            duration: chunkDurationMs,
          });

          // Prune old chunks
          const totalDuration =
            rollingChunksRef.current.length * chunkDurationMs;
          if (totalDuration > rollingBufferConfig.durationMs) {
            // Remove from start until we are within limit
            // Ideally we'd calculate exact byte size or time,
            // but fixed chunk duration + count is a decent approximation.
            const overflow = totalDuration - rollingBufferConfig.durationMs;
            const chunksToRemove = Math.ceil(overflow / chunkDurationMs);
            rollingChunksRef.current.splice(0, chunksToRemove);
          }
        }
      };

      // Start with timeslice to receive frequent chunks
      recorder.start(chunkDurationMs);
      rollingRecorderRef.current = recorder;
    } catch (err) {
      console.error("Failed to start rolling recorder:", err);
    }

    return () => {
      if (
        rollingRecorderRef.current &&
        rollingRecorderRef.current.state !== "inactive"
      ) {
        rollingRecorderRef.current.stop();
      }
    };
  }, [
    rollingBufferConfig.enabled,
    stream,
    getSupportedMimeType,
    rollingBufferConfig.durationMs,
  ]);

  const saveRollingBuffer = useCallback(() => {
    if (rollingChunksRef.current.length === 0) return;

    const mimeType = getSupportedMimeType() || "video/webm";

    // Construct Blob: [Header, ...Chunks]
    // Note: If the first stored chunk was the header, we might double it,
    // but typically we just concat all valid chunks.
    // A more robust approach is to assume the first chunk we EVER got is the header
    // and strictly prepend it if it's not in the current window.
    // For simple implementation: just concat current buffer.

    const blobs = rollingChunksRef.current.map((c) => c.data);

    // If we have a dedicated header blob that fell out of the window, prepend it.
    // NOTE: This can be tricky with some containers.
    // If the header became "stale" or if the stream parameters changed, this might break.
    // But for a continuous stream, preserving the initial initialization segment is key.
    if (headerBlobRef.current && !blobs.includes(headerBlobRef.current)) {
      blobs.unshift(headerBlobRef.current);
    }

    const finalBlob = new Blob(blobs, { type: mimeType });

    const url = URL.createObjectURL(finalBlob);
    const a = document.createElement("a");
    a.style.display = "none";
    a.href = url;
    const ext = mimeType.includes("mp4") ? "mp4" : "webm";
    a.download = `clip-${new Date().toISOString()}.${ext}`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, [getSupportedMimeType]);

  const toggleRollingBuffer = useCallback(
    (enabled: boolean, durationMinutes: number = 2) => {
      setRollingBufferConfig({
        enabled,
        durationMs: durationMinutes * 60 * 1000,
      });
    },
    []
  );

  return {
    isRecording,
    recordingTime,
    startRecording,
    stopRecording,

    // Rolling Buffer
    isRollingBufferActive: rollingBufferConfig.enabled,
    toggleRollingBuffer,
    saveRollingBuffer,
    hasBufferedData: rollingChunksRef.current.length > 0,
  };
}
